import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ_ITEMS = [
  {
    q: "Qu'est-ce que l'Avance immédiate de crédit d'impôt, concrètement ?",
    a: "C'est un service gratuit de l'Urssaf qui vous permet de déduire immédiatement votre crédit d'impôt de 50 % sur la facture que DMS David Multi Service vous adresse. Plus besoin d'attendre l'année suivante : pour 200 € de prestation, vous ne payez que 100 €, tout de suite.",
  },
  {
    q: "Qui peut en bénéficier ?",
    a: "Il faut (1) avoir un compte bancaire domicilié en France, (2) posséder un numéro fiscal associé à votre état civil, et (3) avoir déjà effectué au moins une déclaration de revenus. L'organisme prestataire — DMS dans votre cas — doit aussi être déclaré Services à la Personne (SAP 999 770 779 pour DMS).",
  },
  {
    q: "Comment ça se passe pour moi, pas à pas ?",
    a: "Avec votre autorisation, David vous crée un compte Urssaf en ligne. Vous recevez un mail d'activation. Une fois activé, DMS émet ses demandes de paiement avec 50 % déjà déduits : vous avez 48 h pour les valider sur particulier.urssaf.fr (sans réponse, elles sont validées automatiquement). L'Urssaf prélève votre reste à charge deux jours après, puis verse la totalité à DMS.",
  },
  {
    q: "Et ma déclaration d'impôts alors, je n'oublie rien ?",
    a: "Non — les montants versés via l'Avance immédiate sont automatiquement pré-remplis dans votre déclaration de revenus. Il suffit de vérifier que tout est correct au moment de déclarer.",
  },
  {
    q: "Je recevrai toujours mon attestation fiscale annuelle ?",
    a: "Oui, DMS David Multi Service continuera de vous remettre votre attestation fiscale chaque année, comme d'habitude.",
  },
  {
    q: "Quel est le plafond du crédit d'impôt ?",
    a: "6 000 € de crédit d'impôt par an (soit 12 000 € de dépenses). Pour les personnes en situation de handicap, le plafond passe à 10 000 € par an (soit 20 000 € de dépenses).",
  },
  {
    q: "Comment je suis mon crédit d'impôt disponible ?",
    a: "Depuis la page d'accueil de votre compte particulier.urssaf.fr : vous voyez en temps réel votre plafond annuel, le montant déjà consommé et ce qu'il vous reste.",
  },
  {
    q: "Où je retrouve mes demandes de paiement ?",
    a: "Connectez-vous sur particulier.urssaf.fr, rubrique « Paiement » puis « Tous mes paiements ». Vous y retrouvez l'historique complet.",
  },
  {
    q: "J'ai perdu mon mot de passe Urssaf, je fais comment ?",
    a: "Sur particulier.urssaf.fr, cliquez sur « Me connecter » puis sur « Mot de passe oublié ». Un lien de réinitialisation vous sera envoyé par email.",
  },
  {
    q: "En cas de problème ou question, qui je contacte ?",
    a: "Pour toute question liée au service, appelez d'abord David au 07 44 92 12 08 : en tant que votre prestataire, je suis votre premier interlocuteur. Pour les cas techniques (compte Urssaf, identifiants), l'Urssaf dispose d'un support sur particulier.urssaf.fr.",
  },
];

export const FAQ = () => {
  // Schema.org FAQPage pour le SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a },
    })),
  };

  return (
    <section
      id="faq-avance-immediate"
      className="py-20 bg-white"
      data-testid="faq-section"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#FFCC00]/10 text-[#F0BC00] font-bold px-4 py-1.5 rounded-full text-sm">
            <HelpCircle className="w-4 h-4" />
            FOIRE AUX QUESTIONS
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-[#0F172A] leading-tight">
            Vos questions sur<br className="sm:hidden" /> l'Avance&nbsp;immédiate
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-base">
            Tout ce qu'il faut savoir avant de vous inscrire — réponses officielles reformulées
            par vos soins, sans jargon.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`q-${i}`}
              className="border-b border-gray-200"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left text-base sm:text-lg font-bold text-[#0F172A] hover:text-[#F0BC00] hover:no-underline py-5">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 text-base leading-relaxed pb-5 pr-2">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-10 text-center bg-gradient-to-br from-[#0F172A] to-[#1E4585] text-white rounded-2xl p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-black">Une question non couverte&nbsp;?</h3>
          <p className="mt-2 text-[#D4F0BC]">
            Appelez David, il vous explique simplement et répond à tous vos doutes.
          </p>
          <a
            href="tel:0744921208"
            className="inline-block mt-5 bg-[#FFCC00] hover:bg-[#F0BC00] transition-colors text-white font-bold px-7 py-3.5 rounded-lg text-base shadow-lg"
            data-testid="faq-call-btn"
          >
            📞 07 44 92 12 08
          </a>
        </div>
      </div>
    </section>
  );
};
